import React from 'react';
import { Plus, Trash, X, PencilSimple, BookOpen } from '@phosphor-icons/react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { ArticleEditorModal } from './ArticleEditorModal';

const TVA_OPTIONS = [0, 5, 5.5, 10, 19, 20, 21];
const UNIT_OPTIONS = ['m²', 'm³', 'ml', 'km', 'u', 'Ens', 'Ft', 'h', '-'];

/**
 * Autocomplete description input that proposes library items as the user types.
 * Click on a suggestion fills description + unit + unit_price + tva_rate of the line.
 */
const DescriptionAutocomplete = ({ value, onChange, onPickLibrary, library, idx }) => {
  const [open, setOpen] = React.useState(false);
  const [highlight, setHighlight] = React.useState(0);
  const wrapRef = React.useRef(null);
  const v = String(value || '').trim().toLowerCase();
  const matches = React.useMemo(() => {
    if (!library || !library.length) return [];
    if (!v) return library.slice(0, 8);
    return library
      .filter(li => (li.description || '').toLowerCase().includes(v))
      .slice(0, 8);
  }, [v, library]);

  React.useEffect(() => {
    const onDocClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const pick = (item) => { onPickLibrary(item); setOpen(false); };
  const onKey = (e) => {
    if (!open || matches.length === 0) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setHighlight(h => Math.min(matches.length - 1, h + 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHighlight(h => Math.max(0, h - 1)); }
    else if (e.key === 'Enter') {
      // Only intercept if there's an exact-or-partial match selected and user has typed
      if (v) { e.preventDefault(); pick(matches[highlight]); }
    } else if (e.key === 'Escape') { setOpen(false); }
  };

  return (
    <div ref={wrapRef} style={{ position: 'relative' }} data-testid={`desc-autocomplete-${idx}`}>
      <input
        type="text"
        className="form-input"
        value={value || ''}
        onChange={(e) => { onChange(e.target.value); setOpen(true); setHighlight(0); }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKey}
        placeholder="Description"
        autoComplete="off"
        required
        style={{ fontSize: '0.85rem' }}
      />
      {open && matches.length > 0 && (
        <div
          data-testid={`desc-suggestions-${idx}`}
          style={{
            position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 9999,
            marginTop: 4, background: 'white', border: '1px solid #E2E2E6',
            borderRadius: 8, boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
            maxHeight: 260, overflowY: 'auto'
          }}
        >
          {matches.map((m, i) => (
            <button
              key={m.id}
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => pick(m)}
              onMouseEnter={() => setHighlight(i)}
              style={{
                width: '100%', textAlign: 'left', border: 'none',
                background: i === highlight ? '#F4F5F7' : 'white',
                padding: '8px 10px', cursor: 'pointer', fontSize: '0.82rem',
                display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8
              }}
            >
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>{m.description}</span>
              <span style={{ color: '#5E5F66', fontSize: '0.72rem', whiteSpace: 'nowrap' }}>
                {m.unit ? `${m.unit} - ` : ''}{Number(m.unit_price || 0).toLocaleString('fr-FR')} EUR HT - TVA {m.tva_rate}%
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export const InvoiceFormModal = ({ show, onClose, onSubmit, form, setForm, projects, clients = [], library, modalType, editingItem, addLine, removeLine, updateLine, addFromLibrary, quickAddToLibrary, calcLineHT, calcLineTTC, calcAdvHT, lockProject = false, lockClient = false, forceTvaZero = false, onLibraryChanged = null, previousAdvancements = {} }) => {
  // Article editor state (open from any line via the pencil button)
  const [articleEditor, setArticleEditor] = React.useState({ open: false, lineIdx: -1, item: null });
  const [showLibraryPicker, setShowLibraryPicker] = React.useState(false);
  const [libSearch, setLibSearch] = React.useState('');
  const [libSelected, setLibSelected] = React.useState(new Set());
  const [libGroupByCategory, setLibGroupByCategory] = React.useState(true);

  /**
   * Per-section subtotals. For each header line at index `i`, sum the HT
   * (or advancement_ht for invoices) of all `item` lines between this header
   * and the next header (or end of list). Returns a Map<idx,number>.
   *
   * Headers without items still get an entry (value = 0) so the UI can render
   * a friendly "Sous-total: 0,00 EUR" instead of nothing.
   */
  const subtotalsByHeader = React.useMemo(() => {
    const map = new Map();
    let currentHeaderIdx = -1;
    let currentSum = 0;
    const flush = () => { if (currentHeaderIdx >= 0) map.set(currentHeaderIdx, currentSum); };
    const isInvoice = modalType === 'facture';
    (form.lines || []).forEach((line, idx) => {
      const kind = (line.kind || 'item');
      if (kind === 'header') {
        flush();
        currentHeaderIdx = idx;
        currentSum = 0;
        map.set(idx, 0);
        return;
      }
      if (kind === 'note') return;  // notes never count towards subtotals
      if (currentHeaderIdx < 0) return;  // items above any header are excluded
      // For invoices we sum the advancement HT (what is actually billed this
      // situation). For quotes / standard invoices we sum the full line HT.
      const value = isInvoice ? Number(calcAdvHT(line) || 0) : Number(calcLineHT(line) || 0);
      currentSum += value;
    });
    flush();
    return map;
  }, [form.lines, modalType, calcLineHT, calcAdvHT]);
  const fmtEUR = (n) => (Number(n) || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  // Drag & drop reorder of lines
  const dragLineIdx = React.useRef(null);
  const [dragOverIdx, setDragOverIdx] = React.useState(null);
  const reorderLine = (fromIdx, toIdx) => {
    if (fromIdx === toIdx || fromIdx < 0 || toIdx < 0) return;
    const next = [...form.lines];
    const [moved] = next.splice(fromIdx, 1);
    next.splice(toIdx, 0, moved);
    setForm({ ...form, lines: next });
  };
  const onLineDragStart = (e, idx) => {
    dragLineIdx.current = idx;
    e.dataTransfer.effectAllowed = 'move';
    try { e.dataTransfer.setData('text/plain', String(idx)); } catch {}
  };
  const onLineDragOver = (e, idx) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (dragOverIdx !== idx) setDragOverIdx(idx);
  };
  const onLineDragLeave = () => setDragOverIdx(null);
  const onLineDrop = (e, idx) => {
    e.preventDefault();
    const from = dragLineIdx.current;
    setDragOverIdx(null);
    dragLineIdx.current = null;
    if (from == null) return;
    reorderLine(from, idx);
  };
  // When forceTvaZero is enabled, normalize all line tva_rate to 0 once on each form change
  React.useEffect(() => {
    if (!forceTvaZero || !form?.lines) return;
    const needsUpdate = form.lines.some(ln => Number(ln.tva_rate) !== 0);
    if (needsUpdate) {
      setForm({ ...form, lines: form.lines.map(ln => ({ ...ln, tva_rate: 0 })) });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forceTvaZero, form?.lines?.length]);
  if (!show) return null;
  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div style={{ backgroundColor: 'white', borderRadius: '4px', width: '100%', maxWidth: '95vw', maxHeight: '90vh', overflow: 'auto' }}>
        <div style={{ padding: '20px 24px', borderBottom: '1px solid #E2E2E6', display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'sticky', top: 0, backgroundColor: 'white', zIndex: 10 }}>
          <h3 style={{ margin: 0 }}>
            {editingItem ? `Modifier ${editingItem.number}` : `${modalType === 'devis' ? 'Nouveau devis' : 'Nouvelle facture'}`}
          </h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={24} /></button>
        </div>
        <form onSubmit={onSubmit} style={{ padding: '24px' }}>
          {forceTvaZero && (
            <div data-testid="modal-autoliq-banner" style={{
              background: 'linear-gradient(90deg, #C62828 0%, #B71C1C 100%)',
              color: 'white',
              padding: '10px 14px',
              borderRadius: '8px',
              marginBottom: '18px',
              fontWeight: 700,
              fontSize: '0.9rem',
              letterSpacing: '0.4px',
              textAlign: 'center',
            }}>
              ⚠ TVA 0% AUTOLIQUIDATION ⚠
            </div>
          )}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '20px' }}>
            <div className="form-group">
              <label className="form-label">Chantier {lockProject ? '' : '(optionnel)'}</label>
              {lockProject ? (
                <input
                  type="text"
                  className="form-input"
                  value={(projects.find(p => p.id === form.project_id)?.name) || form.project_id || ''}
                  readOnly
                  disabled
                  data-testid="modal-project-locked"
                  style={{ backgroundColor: 'var(--bg-soft, #F4F5F7)', cursor: 'not-allowed' }}
                />
              ) : (
                <Select value={form.project_id || 'none'} onValueChange={(v) => {
                  if (v === 'none') {
                    setForm({ ...form, project_id: '' });
                    return;
                  }
                  // Auto-affiliate the project's client to the document if no
                  // client has been picked yet (or if the user is creating a
                  // brand new document and just changed project). User keeps
                  // the ability to override afterwards.
                  const proj = projects.find(p => p.id === v) || {};
                  const projectClient = proj.client_id ? (clients.find(c => c.id === proj.client_id) || null) : null;
                  const shouldAutoFill = !form.client_id || form.client_id === '';
                  if (shouldAutoFill && projectClient) {
                    setForm({
                      ...form,
                      project_id: v,
                      client_id: projectClient.id,
                      client_name: projectClient.name || '',
                      client_email: projectClient.email || '',
                      client_phone: projectClient.phone || '',
                      client_address: projectClient.address || '',
                    });
                  } else if (shouldAutoFill && proj.client_name) {
                    // Project has a free-text client name (no contact link).
                    setForm({
                      ...form,
                      project_id: v,
                      client_name: proj.client_name,
                      client_email: proj.client_email || form.client_email,
                      client_phone: proj.client_phone || form.client_phone,
                      client_address: proj.client_address || form.client_address,
                    });
                  } else {
                    setForm({ ...form, project_id: v });
                  }
                }}>
                  <SelectTrigger data-testid="modal-project-select"><SelectValue placeholder="Aucun chantier" /></SelectTrigger>
                  <SelectContent className="z-[9999]">
                    <SelectItem value="none">Aucun chantier</SelectItem>
                    {projects.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
            </div>
            <div className="form-group">
              <label className="form-label">Client {modalType === 'devis' && <span style={{ color: '#C62828' }}>*</span>}</label>
              {lockClient ? (
                <input
                  type="text"
                  className="form-input"
                  value={form.client_name || (clients.find(c => c.id === form.client_id)?.name) || ''}
                  readOnly
                  disabled
                  data-testid="modal-client-locked"
                  style={{ backgroundColor: 'var(--bg-soft, #F4F5F7)', cursor: 'not-allowed' }}
                />
              ) : (
                <Select value={form.client_id || 'none'} onValueChange={(v) => {
                  if (v === 'none') {
                    setForm({ ...form, client_id: '', client_name: '', client_email: '', client_phone: '', client_address: '' });
                  } else {
                    const c = clients.find(cl => cl.id === v);
                    setForm({ ...form, client_id: v, client_name: c?.name || '', client_email: c?.email || '', client_phone: c?.phone || '', client_address: c?.address || '' });
                  }
                }}>
                  <SelectTrigger data-testid="modal-client-select"><SelectValue placeholder="Selectionner un client" /></SelectTrigger>
                  <SelectContent className="z-[9999]">
                    {modalType === 'facture' && <SelectItem value="none">Aucun (saisie libre)</SelectItem>}
                    {clients.length === 0 && <SelectItem value="_empty" disabled>Aucun client dans le repertoire</SelectItem>}
                    {clients.map(c => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.name}{c.company ? ` - ${c.company}` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              {modalType === 'devis' && !lockClient && clients.length === 0 && (
                <small style={{ color: '#C62828', fontSize: '0.75rem' }}>Ajoutez d'abord un client dans Contacts &gt; Clients</small>
              )}
            </div>
            <div className="form-group">
              <label className="form-label">Date *</label>
              <input type="date" className="form-input" value={form.date} onChange={(e) => {
                const newDate = e.target.value;
                const upd = { ...form, date: newDate };
                // Auto-recalc due_date if payment_delay is a fixed value
                if (modalType === 'facture' && form.payment_delay && form.payment_delay !== 'custom' && newDate) {
                  const d = new Date(newDate);
                  d.setDate(d.getDate() + Number(form.payment_delay));
                  upd.due_date = d.toISOString().slice(0, 10);
                }
                setForm(upd);
              }} required />
            </div>
            {modalType === 'devis' && (
              <div className="form-group">
                <label className="form-label">Validite jusqu'au</label>
                <input type="date" className="form-input" value={form.validity_date} onChange={(e) => setForm({ ...form, validity_date: e.target.value })} />
              </div>
            )}
            {modalType === 'facture' && (
              <>
                <div className="form-group">
                  <label className="form-label">Delai de paiement *</label>
                  <Select value={String(form.payment_delay || 30)} onValueChange={(v) => {
                    const upd = { ...form, payment_delay: v === 'custom' ? 'custom' : Number(v) };
                    if (v !== 'custom' && form.date) {
                      const d = new Date(form.date);
                      d.setDate(d.getDate() + Number(v));
                      upd.due_date = d.toISOString().slice(0, 10);
                    }
                    setForm(upd);
                  }}>
                    <SelectTrigger data-testid="modal-payment-delay"><SelectValue /></SelectTrigger>
                    <SelectContent className="z-[9999]">
                      <SelectItem value="15">15 jours</SelectItem>
                      <SelectItem value="30">30 jours</SelectItem>
                      <SelectItem value="45">45 jours</SelectItem>
                      <SelectItem value="60">60 jours</SelectItem>
                      <SelectItem value="custom">Personnalise</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="form-group">
                  <label className="form-label">Date d'echeance <span style={{ color: '#C62828' }}>*</span></label>
                  <input type="date" className="form-input" value={form.due_date || ''} onChange={(e) => setForm({ ...form, due_date: e.target.value, payment_delay: 'custom' })} required data-testid="modal-due-date" />
                </div>
              </>
            )}
            <div className="form-group">
              <label className="form-label">Notes</label>
              <input type="text" className="form-input" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder={modalType === 'devis' ? 'Description...' : 'Situation n...'} />
            </div>
          </div>

          {form.client_id && (
            <div style={{ padding: '10px 14px', backgroundColor: '#F0F9FF', borderRadius: '6px', marginBottom: '20px', border: '1px solid #BAE6FD', fontSize: '0.85rem', color: '#0C4A6E' }}>
              <div style={{ fontWeight: '600', marginBottom: '4px' }}>{form.client_name}</div>
              <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', color: '#075985' }}>
                {form.client_email && <span>Email : {form.client_email}</span>}
                {form.client_phone && <span>Tel : {form.client_phone}</span>}
                {form.client_address && <span>Adresse : {form.client_address}</span>}
              </div>
            </div>
          )}

          {modalType === 'facture' && !form.client_id && (
            <div style={{ padding: '12px', backgroundColor: '#F8F9FB', borderRadius: '6px', marginBottom: '20px', border: '1px solid #E2E2E6' }}>
              <div style={{ fontSize: '0.85rem', fontWeight: '600', marginBottom: '8px', color: '#5E5F66' }}>Informations client (saisie libre)</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '12px' }}>
                <div className="form-group" style={{ margin: 0 }}>
                  <label className="form-label" style={{ fontSize: '0.8rem' }}>Nom du client</label>
                  <input type="text" className="form-input" value={form.client_name || ''} onChange={(e) => setForm({ ...form, client_name: e.target.value })} placeholder="Nom / Raison sociale" data-testid="modal-client-name" />
                </div>
                <div className="form-group" style={{ margin: 0 }}>
                  <label className="form-label" style={{ fontSize: '0.8rem' }}>Email</label>
                  <input type="email" className="form-input" value={form.client_email || ''} onChange={(e) => setForm({ ...form, client_email: e.target.value })} placeholder="email@domaine.fr" data-testid="modal-client-email" />
                </div>
                <div className="form-group" style={{ margin: 0 }}>
                  <label className="form-label" style={{ fontSize: '0.8rem' }}>Telephone</label>
                  <input type="tel" className="form-input" value={form.client_phone || ''} onChange={(e) => setForm({ ...form, client_phone: e.target.value })} placeholder="06 xx xx xx xx" data-testid="modal-client-phone" />
                </div>
                <div className="form-group" style={{ margin: 0 }}>
                  <label className="form-label" style={{ fontSize: '0.8rem' }}>Adresse</label>
                  <input type="text" className="form-input" value={form.client_address || ''} onChange={(e) => setForm({ ...form, client_address: e.target.value })} placeholder="Adresse postale" data-testid="modal-client-address" />
                </div>
              </div>
            </div>
          )}

          {library.length > 0 && (
            <div style={{ marginBottom: '12px', padding: '8px 12px', backgroundColor: '#EFF6FF', border: '1px solid #DBEAFE', borderRadius: '8px', fontSize: '0.78rem', color: '#1E3A8A', display: 'flex', alignItems: 'center', gap: 8 }}>
              <Plus size={14} weight="bold" />
              <span><strong>Astuce :</strong> tapez quelques lettres dans le champ <em>Description</em> pour voir les postes de la bibliotheque ({library.length} disponibles).</span>
            </div>
          )}

          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
              <thead>
                <tr style={{ backgroundColor: '#F4F5F7' }}>
                  <th style={{ padding: '8px', textAlign: 'left', minWidth: '200px' }}>Description</th>
                  <th style={{ padding: '8px', textAlign: 'center', width: '80px' }}>Unite</th>
                  <th style={{ padding: '8px', textAlign: 'right', width: '160px' }}>Prix unit.</th>
                  <th style={{ padding: '8px', textAlign: 'right', width: '170px' }}>Qte</th>
                  <th style={{ padding: '8px', textAlign: 'right', width: '100px' }}>Prix HT</th>
                  <th style={{ padding: '8px', textAlign: 'center', width: '90px' }}>TVA%</th>
                  <th style={{ padding: '8px', textAlign: 'right', width: '100px' }}>Prix TTC</th>
                  {modalType === 'facture' && <th style={{ padding: '8px', textAlign: 'center', width: '80px', backgroundColor: '#FEF3C7' }} title="Avancement cumule des situations precedentes">Avt prec.%</th>}
                  {modalType === 'facture' && <th style={{ padding: '8px', textAlign: 'right', width: '100px', backgroundColor: '#FEF3C7' }} title="Montant HT deja facture sur les situations precedentes">Avt prec. HT</th>}
                  {modalType === 'facture' && <th style={{ padding: '8px', textAlign: 'center', width: '80px' }}>Avt mens.%</th>}
                  {modalType === 'facture' && <th style={{ padding: '8px', textAlign: 'center', width: '80px' }}>Avt total%</th>}
                  {modalType === 'facture' && <th style={{ padding: '8px', textAlign: 'right', width: '100px' }}>Avt HT</th>}
                  <th style={{ padding: '8px', width: '40px' }}></th>
                </tr>
              </thead>
              <tbody>
                {form.lines.map((line, idx) => {
                  const kind = (line.kind || 'item');
                  // Compute total columns count for colspan on special lines
                  const totalCols = modalType === 'facture'
                    ? (Object.values(previousAdvancements || {}).length > 0 || form.lines.some(l => Number(l.previous_advancement || 0) > 0) ? 12 : 10)
                    : 7;
                  if (kind === 'header' || kind === 'note') {
                    const isHeader = kind === 'header';
                    const subtotal = isHeader ? (subtotalsByHeader.get(idx) || 0) : 0;
                    return (
                      <tr
                        key={line._key || `line-${idx}`}
                        data-testid={`special-row-${idx}`}
                        onDragOver={(e) => onLineDragOver(e, idx)}
                        onDragLeave={onLineDragLeave}
                        onDrop={(e) => onLineDrop(e, idx)}
                        style={{ background: isHeader ? '#002FA7' : '#FFFBEB', borderTop: dragOverIdx === idx ? '3px solid #002FA7' : undefined }}
                      >
                        <td colSpan={totalCols} style={{ padding: '8px 12px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <span
                              title="Maintenez clique et glissez pour reordonner"
                              draggable
                              onDragStart={(e) => onLineDragStart(e, idx)}
                              style={{ cursor: 'grab', color: isHeader ? 'rgba(255,255,255,0.8)' : '#92400E', fontSize: '0.9rem', userSelect: 'none', padding: '0 4px' }}
                            >⋮⋮</span>
                            <span style={{ fontWeight: 700, fontSize: '0.72rem', textTransform: 'uppercase', color: isHeader ? 'white' : '#92400E', letterSpacing: 0.6, minWidth: 70 }}>
                              {isHeader ? '☰ Catégorie' : '📝 Note'}
                            </span>
                            <input
                              data-testid={`special-input-${idx}`}
                              value={line.description || ''}
                              onChange={(e) => updateLine(idx, 'description', e.target.value)}
                              placeholder={isHeader ? 'Nom de la catégorie (ex: Démolitions)' : 'Texte de la note'}
                              style={{ flex: 1, border: 'none', outline: 'none', background: 'transparent', color: isHeader ? 'white' : '#1F2937', fontWeight: isHeader ? 700 : 400, fontStyle: isHeader ? 'normal' : 'italic', fontSize: '0.95rem' }}
                            />
                            {isHeader && (
                              <span
                                data-testid={`subtotal-${idx}`}
                                style={{ color: 'white', fontWeight: 700, fontSize: '0.85rem', whiteSpace: 'nowrap', padding: '4px 10px', borderRadius: 6, background: 'rgba(255,255,255,0.16)' }}
                                title="Sous-total HT des lignes de cette catégorie"
                              >
                                Sous-total : {fmtEUR(subtotal)} € HT
                              </span>
                            )}
                            <button
                              type="button"
                              onClick={() => removeLine(idx)}
                              data-testid={`del-special-${idx}`}
                              style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: isHeader ? 'white' : '#92400E', padding: 4 }}
                              title="Supprimer cette ligne"
                            >×</button>
                          </div>
                        </td>
                      </tr>
                    );
                  }
                  const ht = calcLineHT(line);
                  const ttc = calcLineTTC(line);
                  const advHT = calcAdvHT(line);
                  // Resolve previous cumulative advancement for this line
                  // Match priority: source_line_id > line_id > id > index
                  const prevKeys = [line.source_line_id, line.line_id, line.id, String(idx)];
                  let prevPct = 0;
                  for (const k of prevKeys) {
                    if (k != null && previousAdvancements && previousAdvancements[String(k)] != null) {
                      prevPct = Number(previousAdvancements[String(k)]) || 0;
                      break;
                    }
                  }
                  // Fallback: when editing an invoice, the line itself stores `previous_advancement`
                  if (prevPct === 0 && line.previous_advancement != null) {
                    prevPct = Number(line.previous_advancement) || 0;
                  }
                  const prevHT = ht * prevPct / 100;
                  return (
                    <tr
                      key={line._key || `line-${idx}`}
                      onDragOver={(e) => onLineDragOver(e, idx)}
                      onDragLeave={onLineDragLeave}
                      onDrop={(e) => onLineDrop(e, idx)}
                      style={{ borderBottom: '1px solid #E2E2E6', borderTop: dragOverIdx === idx ? '3px solid #002FA7' : undefined }}
                      data-testid={`line-row-${idx}`}
                    >
                      <td style={{ padding: '4px' }}>
                        <div style={{ display: 'flex', gap: 6, alignItems: 'flex-start' }}>
                          <span
                            title="Maintenez clique et glissez pour reordonner"
                            data-testid={`line-drag-${idx}`}
                            draggable
                            onDragStart={(e) => onLineDragStart(e, idx)}
                            style={{ cursor: 'grab', color: '#9CA3AF', fontSize: '1rem', userSelect: 'none', paddingTop: 6, paddingLeft: 2, paddingRight: 2 }}
                          >⋮⋮</span>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <DescriptionAutocomplete
                              idx={idx}
                              value={line.description}
                              library={library}
                              onChange={(val) => updateLine(idx, 'description', val)}
                              onPickLibrary={(item) => {
                                // Update all 5 fields atomically by merging into the line
                                const updated = [...form.lines];
                                updated[idx] = {
                                  ...updated[idx],
                                  description: item.description || updated[idx].description,
                                  details: item.details ?? updated[idx].details,
                                  unit: item.unit || updated[idx].unit,
                                  unit_price: typeof item.unit_price === 'number' ? item.unit_price : updated[idx].unit_price,
                                  tva_rate: typeof item.tva_rate === 'number' ? item.tva_rate : updated[idx].tva_rate,
                                  library_item_id: item.id || updated[idx].library_item_id,
                                };
                                setForm({ ...form, lines: updated });
                              }}
                            />
                          </div>
                          <button
                            type="button"
                            data-testid={`edit-article-btn-${idx}`}
                            title="Editer l'article (composition, marge, prix de vente)"
                            onClick={() => {
                              // Try to find existing library item by description match or library_item_id
                              const desc = (line.description || '').trim().toLowerCase();
                              const libItem = library.find(li => li.id === line.library_item_id) || library.find(li => (li.description || '').trim().toLowerCase() === desc);
                              setArticleEditor({ open: true, lineIdx: idx, item: libItem || null });
                            }}
                            className="btn btn-secondary btn-sm"
                            style={{ padding: '6px 8px', flexShrink: 0 }}
                          >
                            <PencilSimple size={13} />
                          </button>
                        </div>
                        <textarea
                          data-testid={`line-details-${idx}`}
                          className="form-input"
                          rows={2}
                          placeholder="Detail / description longue (s'affiche sous la designation dans le PDF)"
                          value={line.details || ''}
                          onChange={(e) => updateLine(idx, 'details', e.target.value)}
                          style={{ marginTop: 4, fontSize: '0.78rem', fontStyle: 'italic', color: '#4B5563', resize: 'vertical', minHeight: 32 }}
                        />
                        {/* Auto-learning: propose to add to library when description is unique + price > 0 */}
                        {(() => {
                          const desc = (line.description || '').trim();
                          if (!desc || !(Number(line.unit_price) > 0)) return null;
                          const exists = (library || []).some(li => (li.description || '').trim().toLowerCase() === desc.toLowerCase());
                          if (exists) return null;
                          return (
                            <div style={{ marginTop: 4 }}>
                              <button
                                type="button"
                                data-testid={`add-to-library-btn-${idx}`}
                                onClick={() => quickAddToLibrary && quickAddToLibrary(line)}
                                title="Memoriser ce poste dans la bibliotheque pour les prochains documents"
                                style={{
                                  display: 'inline-flex', alignItems: 'center', gap: 4,
                                  padding: '2px 8px', border: '1px dashed #1565C0',
                                  borderRadius: 999, background: 'transparent', color: '#1565C0',
                                  fontSize: '0.7rem', fontWeight: 600, cursor: 'pointer',
                                }}
                              >
                                <Plus size={11} weight="bold" /> Ajouter ce poste a la bibliotheque
                              </button>
                            </div>
                          );
                        })()}
                      </td>
                      <td style={{ padding: '4px' }}>
                        <Select value={line.unit || ''} onValueChange={(v) => updateLine(idx, 'unit', v)}>
                          <SelectTrigger data-testid={`line-unit-${idx}`} style={{ fontSize: '0.85rem', height: '34px', textAlign: 'center' }}>
                            <SelectValue placeholder="-" />
                          </SelectTrigger>
                          <SelectContent className="z-[9999]">
                            {UNIT_OPTIONS.map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </td>
                      <td style={{ padding: '4px' }}>
                        <input type="number" step="0.01" className="form-input" value={line.unit_price} onChange={(e) => updateLine(idx, 'unit_price', parseFloat(e.target.value) || 0)} style={{ fontSize: '1rem', textAlign: 'right', height: 38, width: '100%', minWidth: 110 }} />
                      </td>
                      <td style={{ padding: '4px' }}>
                        <input type="number" step="1" className="form-input" value={line.quantity} onChange={(e) => updateLine(idx, 'quantity', parseFloat(e.target.value) || 0)} style={{ fontSize: '1rem', textAlign: 'right', height: 38, width: '100%', minWidth: 120 }} />
                      </td>
                      <td style={{ padding: '4px', textAlign: 'right', fontWeight: '600', fontSize: '0.85rem' }}>{ht.toLocaleString('fr-FR')} EUR</td>
                      <td style={{ padding: '4px' }}>
                        {forceTvaZero ? (
                          <div
                            title="Pour modifier la TVA, decochez la case 'TVA 0% AUTOLIQUIDATION' dans l'onglet Modifier le chantier."
                            data-testid={`tva-locked-${idx}`}
                            style={{
                              height: '34px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              padding: '0 8px',
                              backgroundColor: '#FFEBEE',
                              border: '1px solid #C62828',
                              borderRadius: '6px',
                              fontWeight: 700,
                              color: '#C62828',
                              fontSize: '0.85rem',
                              cursor: 'help',
                            }}
                          >
                            0%
                          </div>
                        ) : (
                          <Select value={String(line.tva_rate)} onValueChange={(v) => updateLine(idx, 'tva_rate', parseFloat(v))}>
                            <SelectTrigger style={{ fontSize: '0.85rem', height: '34px' }}><SelectValue /></SelectTrigger>
                            <SelectContent className="z-[9999]">
                              {TVA_OPTIONS.map(rate => (
                                <SelectItem key={rate} value={String(rate)}>{rate}%</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                      </td>
                      <td style={{ padding: '4px', textAlign: 'right', fontWeight: '600', fontSize: '0.85rem' }}>{ttc.toLocaleString('fr-FR')} EUR</td>
                      {modalType === 'facture' && (
                        <td style={{ padding: '4px', textAlign: 'center', fontWeight: '600', fontSize: '0.85rem', color: '#92400E', backgroundColor: '#FFFBEB' }}>{prevPct ? `${prevPct}%` : '—'}</td>
                      )}
                      {modalType === 'facture' && (
                        <td style={{ padding: '4px', textAlign: 'right', fontWeight: '600', fontSize: '0.85rem', color: '#92400E', backgroundColor: '#FFFBEB' }}>{prevHT > 0 ? `${prevHT.toLocaleString('fr-FR')} EUR` : '—'}</td>
                      )}
                      {modalType === 'facture' && (
                        <td style={{ padding: '4px' }}>
                          <input type="number" step="1" min="0" max="100" className="form-input" value={line.monthly_advancement} onChange={(e) => updateLine(idx, 'monthly_advancement', parseFloat(e.target.value) || 0)} style={{ fontSize: '0.85rem', textAlign: 'center' }} />
                        </td>
                      )}
                      {modalType === 'facture' && (
                        <td style={{ padding: '4px' }}>
                          <input type="number" step="1" min="0" max="100" className="form-input" value={line.total_advancement} onChange={(e) => updateLine(idx, 'total_advancement', parseFloat(e.target.value) || 0)} style={{ fontSize: '0.85rem', textAlign: 'center', fontWeight: '700', color: '#002FA7' }} />
                        </td>
                      )}
                      {modalType === 'facture' && <td style={{ padding: '4px', textAlign: 'right', fontWeight: '700', fontSize: '0.85rem', color: '#2E7D32' }}>{advHT.toLocaleString('fr-FR')} EUR</td>}
                      <td style={{ padding: '4px' }}>
                        {form.lines.length > 1 && (
                          <button type="button" className="btn btn-danger btn-sm" onClick={() => removeLine(idx)} style={{ padding: '4px' }}><Trash size={12} /></button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr style={{ backgroundColor: '#F4F5F7', fontWeight: '700' }}>
                  <td colSpan={4} style={{ padding: '8px' }}>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      <button type="button" className="btn btn-secondary btn-sm" onClick={addLine}><Plus size={14} /> Ajouter un poste</button>
                      <button type="button" className="btn btn-primary btn-sm" data-testid="add-from-library-btn" onClick={() => setShowLibraryPicker(true)}>
                        <Plus size={14} /> Choisir dans la bibliothèque
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm"
                        data-testid="add-category-line-btn"
                        onClick={() => {
                          const newLine = {
                            id: `tmp-h-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
                            kind: 'header',
                            description: '',
                          };
                          setForm({ ...form, lines: [...(form.lines || []), newLine] });
                        }}
                        style={{ background: '#002FA7', color: 'white', fontWeight: 600 }}
                        title="Insère une ligne titre de catégorie"
                      >
                        <Plus size={14} /> Ajouter une catégorie
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm"
                        data-testid="add-note-line-btn"
                        onClick={() => {
                          const newLine = {
                            id: `tmp-n-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
                            kind: 'note',
                            description: '',
                          };
                          setForm({ ...form, lines: [...(form.lines || []), newLine] });
                        }}
                        style={{ background: '#FBBF24', color: '#1F2937', fontWeight: 600 }}
                        title="Insère une ligne note libre"
                      >
                        <Plus size={14} /> Ajouter une note
                      </button>
                    </div>
                  </td>
                  <td style={{ padding: '8px', textAlign: 'right' }}>{(form.lines.reduce((s, ln) => s + calcLineHT(ln), 0) || 0).toLocaleString('fr-FR')} EUR</td>
                  <td></td>
                  <td style={{ padding: '8px', textAlign: 'right' }}>{(form.lines.reduce((s, ln) => s + calcLineTTC(ln), 0) || 0).toLocaleString('fr-FR')} EUR</td>
                  {modalType === 'facture' && <td></td>}
                  {modalType === 'facture' && (
                    <td style={{ padding: '8px', textAlign: 'right', color: '#92400E', backgroundColor: '#FFFBEB' }}>
                      {(form.lines.reduce((s, ln, i) => {
                        const ks = [ln.source_line_id, ln.line_id, ln.id, String(i)];
                        let p = 0;
                        for (const k of ks) { if (k != null && previousAdvancements && previousAdvancements[String(k)] != null) { p = Number(previousAdvancements[String(k)]) || 0; break; } }
                        if (p === 0 && ln.previous_advancement != null) p = Number(ln.previous_advancement) || 0;
                        return s + (calcLineHT(ln) * p / 100);
                      }, 0) || 0).toLocaleString('fr-FR')} EUR
                    </td>
                  )}
                  {modalType === 'facture' && <td colSpan={2}></td>}
                  {modalType === 'facture' && <td style={{ padding: '8px', textAlign: 'right', color: '#2E7D32' }}>{(form.lines.reduce((s, ln) => s + calcAdvHT(ln), 0) || 0).toLocaleString('fr-FR')} EUR</td>}
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </div>

          <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
            <button type="button" className="btn btn-secondary" onClick={onClose} style={{ flex: 1 }}>Annuler</button>
            <button type="submit" className="btn btn-primary" style={{ flex: 1 }} data-testid="submit-doc">
              {editingItem ? 'Enregistrer' : `Creer ${modalType === 'devis' ? 'le devis' : 'la facture'}`}
            </button>
          </div>
        </form>
      </div>
      <ArticleEditorModal
        show={articleEditor.open}
        item={articleEditor.item}
        onClose={() => {
          // When the user cancels the new-article modal opened from the
          // library picker, reopen the picker to let them continue selecting.
          const reopenPicker = articleEditor.lineIdx === -2;
          setArticleEditor({ open: false, lineIdx: -1, item: null });
          if (reopenPicker) setShowLibraryPicker(true);
        }}
        onSaved={(saved) => {
          if (onLibraryChanged) onLibraryChanged();
          // Created from "Nouvel article" inside the library picker?
          // → reopen the picker and auto-select the freshly created article
          //   (the parent's onLibraryChanged refresh repopulates the list).
          if (articleEditor.lineIdx === -2 && saved && saved.id) {
            setLibSelected((prev) => {
              const next = new Set(prev);
              next.add(saved.id);
              return next;
            });
            setShowLibraryPicker(true);
          }
        }}
        onLineFill={(saved) => {
          // Update the source line with the saved article values
          if (articleEditor.lineIdx >= 0) {
            const updated = [...form.lines];
            updated[articleEditor.lineIdx] = {
              ...updated[articleEditor.lineIdx],
              description: saved.description || updated[articleEditor.lineIdx].description,
              details: saved.details ?? updated[articleEditor.lineIdx].details,
              unit: saved.unit || updated[articleEditor.lineIdx].unit,
              unit_price: typeof saved.unit_price === 'number' ? saved.unit_price : updated[articleEditor.lineIdx].unit_price,
              tva_rate: typeof saved.tva_rate === 'number' ? saved.tva_rate : updated[articleEditor.lineIdx].tva_rate,
              library_item_id: saved.id || updated[articleEditor.lineIdx].library_item_id,
            };
            setForm({ ...form, lines: updated });
          }
        }}
      />
      {showLibraryPicker && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100 }}>
          <div style={{ background: 'white', borderRadius: 12, width: '100%', maxWidth: 820, maxHeight: '85vh', display: 'flex', flexDirection: 'column' }}>
            <div style={{ padding: 16, borderBottom: '1px solid #E2E2E6', display: 'flex', alignItems: 'center', gap: 10 }}>
              <h3 style={{ margin: 0, fontSize: '1rem' }}>Choisir des articles de la bibliothèque</h3>
              <span style={{ fontSize: '0.78rem', color: '#5E5F66', marginLeft: 8 }} data-testid="lib-picker-count">{libSelected.size} sélectionné(s)</span>
              <button onClick={() => { setShowLibraryPicker(false); setLibSearch(''); setLibSelected(new Set()); }} style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', fontSize: 22 }} data-testid="lib-picker-close">×</button>
            </div>
            <div style={{ padding: 12, display: 'flex', gap: 8, alignItems: 'center' }}>
              <input
                type="text" autoFocus className="form-input"
                placeholder="Rechercher un article..."
                value={libSearch}
                onChange={(e) => setLibSearch(e.target.value)}
                data-testid="lib-picker-search"
                style={{ flex: 1 }}
              />
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: '0.8rem', cursor: 'pointer' }}>
                <input type="checkbox" checked={libGroupByCategory} onChange={(e) => setLibGroupByCategory(e.target.checked)} />
                Grouper par catégorie
              </label>
              <button
                type="button"
                data-testid="lib-picker-new-article-btn"
                onClick={() => {
                  // Open the ArticleEditorModal in create mode. On save, we
                  // reload the library so the new article appears in the list
                  // and we auto-select it for the user.
                  setShowLibraryPicker(false);
                  setArticleEditor({ open: true, lineIdx: -2, item: null });
                }}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '6px 10px', borderRadius: 6, border: '1px solid #1D4ED8', background: '#EFF6FF', color: '#1D4ED8', fontWeight: 600, fontSize: '0.82rem', cursor: 'pointer' }}
              >
                <Plus size={14} /> Nouvel article
              </button>
            </div>
            <div style={{ flex: 1, overflowY: 'auto', padding: '0 12px 12px' }}>
              {(() => {
                const q = libSearch.trim().toLowerCase();
                const list = (library || []).filter(it =>
                  !q || (it.description || '').toLowerCase().includes(q) || (it.category || '').toLowerCase().includes(q)
                );
                if (list.length === 0) return <p style={{ color: '#5E5F66', fontSize: '0.85rem' }}>Aucun article trouvé.</p>;

                const renderRow = (it) => (
                  <label
                    key={it.id}
                    data-testid={`lib-pick-${it.id}`}
                    style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', textAlign: 'left', padding: '10px 12px', border: '1px solid #E2E2E6', borderRadius: 8, marginBottom: 6, background: libSelected.has(it.id) ? '#EFF6FF' : 'white', cursor: 'pointer' }}
                  >
                    <input
                      type="checkbox"
                      checked={libSelected.has(it.id)}
                      onChange={() => {
                        setLibSelected(prev => {
                          const next = new Set(prev);
                          if (next.has(it.id)) next.delete(it.id); else next.add(it.id);
                          return next;
                        });
                      }}
                    />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600, fontSize: '0.9rem' }}>{it.description}</div>
                      <div style={{ fontSize: '0.75rem', color: '#5E5F66', marginTop: 2 }}>
                        {it.category && <span style={{ marginRight: 8 }}>{it.category}</span>}
                        <strong>{(it.unit_price || 0).toFixed(2)} €</strong> /{it.unit} · TVA {it.tva_rate}%
                      </div>
                    </div>
                  </label>
                );

                if (libGroupByCategory) {
                  const groups = {};
                  list.forEach(it => {
                    const cat = it.category || '(Sans catégorie)';
                    if (!groups[cat]) groups[cat] = [];
                    groups[cat].push(it);
                  });
                  const cats = Object.keys(groups).sort((a, b) => a.localeCompare(b));
                  return cats.map(cat => (
                    <div key={cat} style={{ marginBottom: 12 }}>
                      <div style={{ padding: '6px 10px', background: '#F1F5F9', borderRadius: 6, fontWeight: 700, fontSize: '0.85rem', color: '#0F172A', marginBottom: 6 }}>
                        {cat} <span style={{ fontWeight: 400, fontSize: '0.75rem', color: '#5E5F66' }}>({groups[cat].length})</span>
                      </div>
                      {groups[cat].map(renderRow)}
                    </div>
                  ));
                }
                return list.map(renderRow);
              })()}
            </div>
            <div style={{ padding: 12, borderTop: '1px solid #E2E2E6', display: 'flex', justifyContent: 'space-between', gap: 8 }}>
              <button type="button" className="btn btn-secondary" onClick={() => { setShowLibraryPicker(false); setLibSearch(''); setLibSelected(new Set()); }}>Annuler</button>
              <button
                type="button"
                data-testid="lib-picker-add-btn"
                className="btn btn-primary"
                disabled={libSelected.size === 0}
                onClick={() => {
                  const toAdd = (library || []).filter(it => libSelected.has(it.id));
                  // Group by category then insert header + items per group
                  const byCat = {};
                  toAdd.forEach(it => {
                    const cat = (it.category || '').trim() || 'Sans catégorie';
                    if (!byCat[cat]) byCat[cat] = [];
                    byCat[cat].push(it);
                  });
                  const newLines = [];
                  Object.keys(byCat).sort().forEach((cat) => {
                    newLines.push({
                      id: `tmp-h-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
                      kind: 'header',
                      description: cat,
                    });
                    byCat[cat].forEach((it) => {
                      newLines.push({
                        id: `tmp-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
                        kind: 'item',
                        description: it.description || '',
                        details: it.details || '',
                        unit: it.unit || 'u',
                        unit_price: Number(it.unit_price) || 0,
                        quantity: 1,
                        tva_rate: forceTvaZero ? 0 : (Number(it.tva_rate) ?? 20),
                        monthly_advancement: 0,
                        total_advancement: modalType === 'facture' ? 100 : 0,
                        library_item_id: it.id,
                      });
                    });
                  });
                  setForm({ ...form, lines: [...(form.lines || []), ...newLines] });
                  setShowLibraryPicker(false);
                  setLibSearch('');
                  setLibSelected(new Set());
                }}
              >
                Ajouter {libSelected.size > 0 ? `(${libSelected.size})` : ''}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export const LibraryModal = ({ show, onClose, library, onDelete, onRefresh }) => {
  const [editing, setEditing] = React.useState(null);
  const [showEditor, setShowEditor] = React.useState(false);
  const [filterCat, setFilterCat] = React.useState('all');
  if (!show) return null;
  const categories = Array.from(new Set((library || []).map(l => l.category).filter(Boolean))).sort();
  const filtered = filterCat === 'all'
    ? library
    : (filterCat === 'none'
      ? library.filter(l => !l.category)
      : library.filter(l => l.category === filterCat));
  return (
    <div data-testid="library-modal" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div style={{ backgroundColor: 'white', borderRadius: '8px', width: '100%', maxWidth: '1000px', maxHeight: '90vh', overflow: 'auto' }}>
        <div style={{ padding: '20px 24px', borderBottom: '1px solid #E2E2E6', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
          <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}><BookOpen size={20} /> Bibliotheque des articles</h3>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <Select value={filterCat} onValueChange={setFilterCat}>
              <SelectTrigger data-testid="library-filter-category" style={{ width: 200, height: 34, fontSize: '0.85rem' }}>
                <SelectValue placeholder="Toutes categories" />
              </SelectTrigger>
              <SelectContent className="z-[9999]">
                <SelectItem value="all">Toutes categories</SelectItem>
                <SelectItem value="none">Sans categorie</SelectItem>
                {categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
            <button className="btn btn-primary btn-sm" data-testid="library-new-article-btn" onClick={() => { setEditing(null); setShowEditor(true); }}>
              <Plus size={14} /> Nouvel article
            </button>
            <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={24} /></button>
          </div>
        </div>
        <div style={{ padding: '20px 24px' }}>
          {filtered.length > 0 ? (
            <table className="data-table" data-testid="library-table">
              <thead>
                <tr>
                  <th>Designation</th>
                  <th>Categorie</th>
                  <th>Unite</th>
                  <th style={{ textAlign: 'right' }}>Prix de revient</th>
                  <th style={{ textAlign: 'right' }}>Marge</th>
                  <th style={{ textAlign: 'right' }}>Prix de vente HT</th>
                  <th>TVA%</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(item => (
                  <tr key={item.id} data-testid={`lib-row-${item.id}`}>
                    <td style={{ fontWeight: '600' }}>
                      {item.description}
                      {item.details && <div style={{ fontSize: '0.75rem', color: '#6B7280', fontStyle: 'italic', fontWeight: 400, marginTop: 2 }}>{item.details}</div>}
                    </td>
                    <td>{item.category ? <span style={{ padding: '2px 8px', background: '#EFF6FF', color: '#1D4ED8', borderRadius: 4, fontSize: '0.74rem', fontWeight: 600 }}>{item.category}</span> : <span style={{ color: '#9CA3AF' }}>-</span>}</td>
                    <td>{item.unit}</td>
                    <td style={{ textAlign: 'right', color: '#5E5F66' }}>{(item.cost_price_ht || 0).toFixed(2)} EUR</td>
                    <td style={{ textAlign: 'right' }}>{item.margin_percent ?? 30}%</td>
                    <td style={{ textAlign: 'right', fontWeight: '700', color: '#047857' }}>{(item.unit_price || 0).toFixed(2)} EUR</td>
                    <td>{item.tva_rate}%</td>
                    <td>
                      <div style={{ display: 'flex', gap: '4px' }}>
                        <button data-testid={`lib-edit-${item.id}`} className="btn btn-secondary btn-sm" onClick={() => { setEditing(item); setShowEditor(true); }}>
                          <PencilSimple size={12} />
                        </button>
                        <button className="btn btn-danger btn-sm" onClick={() => onDelete(item.id)}><Trash size={12} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p style={{ textAlign: 'center', color: '#5E5F66', padding: '20px' }}>
              Aucun article. Cliquez sur "Nouvel article" pour en creer un.
            </p>
          )}
        </div>
      </div>
      <ArticleEditorModal
        show={showEditor}
        item={editing}
        onClose={() => { setShowEditor(false); setEditing(null); }}
        onSaved={() => { if (onRefresh) onRefresh(); }}
      />
    </div>
  );
};
